import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Progress } from '../../components/ui/progress';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Upload, FileText, Trash2, Download, Search, Eye, X, Settings, MessageCircle,
  CheckCircle, Clock, AlertCircle, BarChart3, ChevronDown, ChevronUp, Pencil,
  Heart, Utensils, CalendarDays, Stethoscope, Activity
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_WHATSAPP_TEMPLATE = 'Hi {patient_name}, thank you for choosing {lab_name}! Your health report is ready to download:\n{download_url}\n\n{tagline}\n\nContact us: {contact}';

const buildWhatsAppMessage = (report, letterhead) => {
  const template = letterhead?.whatsapp_message_template || DEFAULT_WHATSAPP_TEMPLATE;
  const vars = {
    patient_name: report.patient_name || '',
    lab_name: letterhead?.lab_name || '',
    tagline: letterhead?.tagline || '',
    download_url: `${API}/api/lis/reports/public/${report.id}`,
    contact: [letterhead?.phone, letterhead?.email].filter(Boolean).join(' | '),
    review_link: letterhead?.google_review_link || ''
  };
  let msg = template;
  Object.entries(vars).forEach(([k, v]) => { msg = msg.split(`{${k}}`).join(v); });
  if (letterhead?.google_review_link && !template.includes('{review_link}')) {
    msg += `\n\nRate us on Google & get FLAT 10% DISCOUNT on your next test booking! ${letterhead.google_review_link}`;
  }
  return msg;
};

export default function ReportUpload() {
  const token = localStorage.getItem('vendor_token');
  const headers = { Authorization: `Bearer ${token}` };
  const fileInputRef = useRef(null);

  const [stats, setStats] = useState({ total_reports: 0, completed: 0, processing: 0, pending: 0 });
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [labFilter, setLabFilter] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [processingLabs, setProcessingLabs] = useState([]);
  const [pendingBills, setPendingBills] = useState([]);
  const [viewFilter, setViewFilter] = useState('all'); // all | uploaded | not_uploaded | sent | not_sent
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({}); // { [key]: { name, percent } }
  const [selectedLabForUpload, setSelectedLabForUpload] = useState('');
  const [uploadPhone, setUploadPhone] = useState('');
  const [editPhone, setEditPhone] = useState(null); // { report, value }
  const [showSettings, setShowSettings] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [enabled, setEnabled] = useState(true);

  // Letterhead profile ("my letterhead") state
  const [letterhead, setLetterhead] = useState(null);
  const [lhForm, setLhForm] = useState({
    lab_name: '', tagline: '', accreditations: '', address: '', phone: '', email: '', brand_color: '#334155',
    footer_left_text: '', footer_right_text: '', google_review_link: '',
    whatsapp_message_template: ''
  });
  const [lhLogoFile, setLhLogoFile] = useState(null);
  const [lhAccredLogo1File, setLhAccredLogo1File] = useState(null);
  const [lhAccredLogo2File, setLhAccredLogo2File] = useState(null);
  const [lhTermsFile, setLhTermsFile] = useState(null);
  const [savingLetterhead, setSavingLetterhead] = useState(false);
  const [uploadingTerms, setUploadingTerms] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/lis/reports/stats`, { headers });
      if (res.status === 403) { setEnabled(false); return; }
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
  }, [token]);

  const fetchReports = useCallback(async () => {
    const params = new URLSearchParams({ page, limit: 20 });
    if (labFilter) params.append('lab_type', labFilter);
    if (search) params.append('search', search);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (viewFilter === 'sent') params.append('whatsapp_status', 'sent');
    if (viewFilter === 'not_sent') params.append('whatsapp_status', 'not_sent');
    try {
      const res = await fetch(`${API}/api/lis/reports?${params}`, { headers });
      if (res.ok) { const d = await res.json(); setReports(d.reports || []); setTotal(d.total || 0); }
    } catch (e) { console.error(e); }
  }, [page, labFilter, search, dateFrom, dateTo, viewFilter, token]);

  const fetchMeta = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    const [labsRes, lhRes, pendRes] = await Promise.all([
      fetch(`${API}/api/lis/processing-labs`, { headers: h }),
      fetch(`${API}/api/lis/letterhead-settings`, { headers: h }),
      fetch(`${API}/api/lis/reports/pending-from-bills`, { headers: h })
    ]);
    if (labsRes.ok) {
      const labs = await labsRes.json();
      const safeLabs = Array.isArray(labs) ? labs.filter(l => l && l.name) : [];
      setProcessingLabs(safeLabs);
      if (safeLabs.length > 0 && !selectedLabForUpload) setSelectedLabForUpload(safeLabs[0].name);
    }
    if (lhRes.ok) {
      const lh = await lhRes.json();
      setLetterhead(lh && lh.vendor_id ? lh : null);
      if (lh && lh.vendor_id) {
        setLhForm({
          lab_name: lh.lab_name || '', tagline: lh.tagline || '', accreditations: lh.accreditations || '',
          address: lh.address || '', phone: lh.phone || '', email: lh.email || '', brand_color: lh.brand_color || '#334155',
          footer_left_text: lh.footer_left_text || '', footer_right_text: lh.footer_right_text || '',
          google_review_link: lh.google_review_link || '',
          whatsapp_message_template: lh.whatsapp_message_template || DEFAULT_WHATSAPP_TEMPLATE
        });
      }
    }
    if (pendRes.ok) setPendingBills(await pendRes.json());
  }, [token]);

  useEffect(() => { fetchStats(); fetchMeta(); }, [fetchStats, fetchMeta]);
  useEffect(() => { fetchReports(); }, [fetchReports]);

  // Poll for processing reports
  useEffect(() => {
    if (stats.processing > 0) {
      const interval = setInterval(() => { fetchReports(); fetchStats(); }, 3000);
      return () => clearInterval(interval);
    }
  }, [stats.processing, fetchReports, fetchStats]);

  // Live elapsed-time ticker to drive a simulated progress percentage while AI analysis runs
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (!reports.some(r => r.status === 'processing')) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [reports]);

  const PROCESSING_ESTIMATE_SECONDS = 18;
  const getProcessingPercent = (report) => {
    const started = report.created_at ? new Date(report.created_at).getTime() : nowTick;
    const elapsed = Math.max(0, (nowTick - started) / 1000);
    return Math.min(96, Math.round((elapsed / PROCESSING_ESTIMATE_SECONDS) * 96));
  };

  const handleFileDrop = async (e) => {
    e.preventDefault();
    const files = e.dataTransfer?.files || e.target?.files;
    if (!files?.length) return;
    await uploadFiles(Array.from(files));
  };

  const uploadWithProgress = (url, formData, onProgress) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress(100);
          let doc = null;
          try { doc = JSON.parse(xhr.responseText); } catch (err) { /* ignore */ }
          resolve(doc);
        }
        else {
          let msg = 'Upload failed';
          try { msg = JSON.parse(xhr.responseText).detail || msg; } catch (err) { /* ignore */ }
          reject(msg);
        }
      };
      xhr.onerror = () => reject('Network error');
      xhr.send(formData);
    });
  };

  const uploadFiles = async (files) => {
    const pdfFiles = Array.from(files || []).filter(f => {
      if (!f?.name || !f.name.toLowerCase().endsWith('.pdf')) { toast.error(`${f?.name || 'File'}: Only PDF files allowed`); return false; }
      return true;
    });
    if (!pdfFiles.length) return;
    setUploading(true);
    const results = await Promise.all(pdfFiles.map((file, idx) => {
      const key = `${file.name}-${idx}-${Date.now()}`;
      setUploadProgress(prev => ({ ...prev, [key]: { name: file.name, percent: 0 } }));
      const formData = new FormData();
      formData.append('file', file);
      formData.append('lab_type', selectedLabForUpload || '');
      if (uploadPhone.trim()) formData.append('patient_phone', uploadPhone.trim());
      return uploadWithProgress(`${API}/api/lis/reports/upload`, formData, (pct) => {
        setUploadProgress(prev => ({ ...prev, [key]: { name: file.name, percent: pct } }));
      }).then((doc) => {
        setTimeout(() => setUploadProgress(prev => { const n = { ...prev }; delete n[key]; return n; }), 400);
        return doc;
      }).catch((errMsg) => {
        toast.error(`${file.name}: ${typeof errMsg === 'string' ? errMsg : (errMsg?.message || 'Upload failed')}`);
        setUploadProgress(prev => { const n = { ...prev }; delete n[key]; return n; });
        return null;
      });
    }));
    const newDocs = results.filter(Boolean);
    if (newDocs.length > 0) {
      toast.success(`${newDocs.length} report${newDocs.length > 1 ? 's' : ''} uploaded. AI analysis in progress...`);
      // Show the new reports instantly - no need to wait for a full re-fetch
      setReports(prev => [...newDocs, ...prev]);
      setTotal(prev => prev + newDocs.length);
      setStats(prev => ({ ...prev, total_reports: prev.total_reports + newDocs.length, processing: prev.processing + newDocs.length }));
      fetchMeta();
    }
    setUploading(false);
  };

  const uploadForBill = (bill) => {
    if (!fileInputRef.current) return;
    fileInputRef.current.click();
    fileInputRef.current.onchange = async (e) => {
      const files = e.target.files;
      const file = files && files[0];
      if (!file) return;
      setUploading(true);
      const key = `bill-${bill.id}`;
      setUploadProgress(prev => ({ ...prev, [key]: { name: file.name || 'report.pdf', percent: 0 } }));
      const formData = new FormData();
      formData.append('file', file);
      formData.append('lab_type', bill.processing_lab || selectedLabForUpload || '');
      formData.append('bill_id', bill.id);
      formData.append('description', `Bill #${bill.reg_no}`);
      try {
        const doc = await uploadWithProgress(`${API}/api/lis/reports/upload`, formData, (pct) => {
          setUploadProgress(prev => ({ ...prev, [key]: { name: file.name || 'report.pdf', percent: pct } }));
        });
        toast.success('Report uploaded for bill');
        if (doc) {
          setReports(prev => [doc, ...prev]);
          setTotal(prev => prev + 1);
          setStats(prev => ({ ...prev, total_reports: prev.total_reports + 1, processing: prev.processing + 1 }));
        }
        fetchMeta();
      } catch (errMsg) {
        toast.error(typeof errMsg === 'string' ? errMsg : (errMsg?.message || 'Upload failed'));
      }
      finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setUploadProgress(prev => { const n = { ...prev }; delete n[key]; return n; });
      }
    };
  };

  const saveLetterhead = async () => {
    if (!lhForm.lab_name.trim()) { toast.error('Lab name is required'); return; }
    setSavingLetterhead(true);
    const formData = new FormData();
    Object.entries(lhForm).forEach(([k, v]) => formData.append(k, v));
    if (lhLogoFile) formData.append('logo', lhLogoFile);
    if (lhAccredLogo1File) formData.append('accreditation_logo_1', lhAccredLogo1File);
    if (lhAccredLogo2File) formData.append('accreditation_logo_2', lhAccredLogo2File);
    try {
      const res = await fetch(`${API}/api/lis/letterhead-settings`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData
      });
      if (res.ok) {
        toast.success('Letterhead saved. New uploads will use this branding automatically.');
        setLhLogoFile(null); setLhAccredLogo1File(null); setLhAccredLogo2File(null); fetchMeta();
      } else { const err = await res.json(); toast.error(err.detail || 'Failed to save'); }
    } finally { setSavingLetterhead(false); }
  };

  const onLogoSelected = async (file) => {
    setLhLogoFile(file);
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/api/lis/letterhead-settings/detect-color`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData
      });
      if (res.ok) {
        const { color } = await res.json();
        if (color) setLhForm(prev => ({ ...prev, brand_color: color }));
      }
    } catch (e) { /* color detection is a nice-to-have, ignore failures */ }
  };

  const uploadTermsPage = async () => {
    if (!lhTermsFile) { toast.error('Select a file'); return; }
    setUploadingTerms(true);
    const formData = new FormData();
    formData.append('file', lhTermsFile);
    try {
      const res = await fetch(`${API}/api/lis/letterhead-settings/terms-page`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData
      });
      if (res.ok) { toast.success('Terms & Conditions page saved'); setLhTermsFile(null); fetchMeta(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } finally { setUploadingTerms(false); }
  };

  const viewReport = async (report) => {
    setSelectedReport(report);
    // If report is still processing, just show the report details
    if (!report.processed) {
      toast.info('AI analysis is still in progress for this report. Please check back shortly.');
      return;
    }
  };

  const [previewLoading, setPreviewLoading] = useState(false);

  const openPreview = async (report) => {
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      const res = await fetch(`${API}/api/lis/reports/download/${report.id}`, { headers });
      if (res.ok) {
        const blob = await res.blob();
        setPreviewUrl(URL.createObjectURL(blob));
      } else {
        toast.error('Failed to load preview. Please try again.');
      }
    } catch (e) { toast.error('Failed to load preview'); }
    finally { setPreviewLoading(false); }
  };

  const shareViaWhatsApp = (report) => {
    const phone = (report.patient_phone || '').replace(/\D/g, '');
    if (!phone) { toast.error('No patient phone number found for this report'); return; }
    const message = buildWhatsAppMessage(report, letterhead);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
    if (!report.whatsapp_sent) {
      fetch(`${API}/api/lis/reports/${report.id}/whatsapp-sent`, { method: 'PUT', headers }).catch(() => {});
      setReports(prev => prev.map(r => r.id === report.id ? { ...r, whatsapp_sent: true } : r));
    }
  };

  const dismissPendingBill = async (billId) => {
    await fetch(`${API}/api/lis/reports/pending-from-bills/${billId}/dismiss`, { method: 'PUT', headers });
    setPendingBills(prev => prev.filter(b => b.id !== billId));
  };

  const clearAllPendingBills = async () => {
    if (!window.confirm(`Clear all ${pendingBills.length} pending items? They won't be flagged for report upload anymore.`)) return;
    const res = await fetch(`${API}/api/lis/reports/pending-from-bills/clear-all`, { method: 'POST', headers });
    if (res.ok) { toast.success('Cleared pending list'); setPendingBills([]); }
    else toast.error('Failed to clear');
  };

  const downloadReport = async (report) => {
    try {
      const res = await fetch(`${API}/api/lis/reports/download/${report.id}`, { headers });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const namePart = (report.patient_name || report.original_filename || 'Report').replace(/[\\/:*?"<>|]/g, '').trim();
        const filename = `${report.reg_no ? `${report.reg_no}_` : ''}${namePart}.pdf`;
        a.href = url; a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else {
        toast.error('Download failed. Please try again.');
      }
    } catch (e) { toast.error('Download failed'); }
  };

  const deleteReport = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    const res = await fetch(`${API}/api/lis/reports/${id}`, { method: 'DELETE', headers });
    if (res.ok) { toast.success('Deleted'); fetchReports(); fetchStats(); if (selectedReport?.id === id) setSelectedReport(null); }
  };

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedIds(prev => prev.length === reports.length ? [] : reports.map(r => r.id));

  const bulkDeleteReports = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected report(s)?`)) return;
    const res = await fetch(`${API}/api/lis/reports/bulk-delete`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: selectedIds }) });
    if (res.ok) { const d = await res.json(); toast.success(`Deleted ${d.deleted} report(s)`); setSelectedIds([]); fetchReports(); fetchStats(); }
    else toast.error('Bulk delete failed');
  };

  const saveEditPhone = async () => {
    if (!editPhone?.value?.trim()) { toast.error('Enter a phone number'); return; }
    const res = await fetch(`${API}/api/lis/reports/${editPhone.report.id}/patient-phone`, {
      method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ patient_phone: editPhone.value.trim() })
    });
    if (res.ok) { toast.success('Phone number updated'); setEditPhone(null); fetchReports(); }
    else toast.error('Failed to update phone number');
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!enabled) {
    return (
      <div data-testid="report-upload-disabled" className="max-w-2xl mx-auto text-center py-16">
        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4"><Upload className="w-8 h-8 text-slate-400" /></div>
        <h2 className="text-lg font-semibold text-slate-800 mb-2">Report Upload Not Enabled</h2>
        <p className="text-sm text-slate-500">Contact your admin to enable the report upload feature for your lab.</p>
      </div>
    );
  }

  return (
    <div data-testid="report-upload-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-slate-800">Lab Reports</h1>
        <Button variant="outline" size="sm" onClick={() => setShowSettings(true)} data-testid="report-settings-btn">
          <Settings className="w-4 h-4 mr-1" /> Settings
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card className="border-slate-200"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"><BarChart3 className="w-5 h-5 text-blue-600" /></div>
          <div><p className="text-2xl font-bold text-slate-800">{stats.total_reports}</p><p className="text-xs text-slate-500">Total Reports</p></div>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center"><CheckCircle className="w-5 h-5 text-emerald-600" /></div>
          <div><p className="text-2xl font-bold text-emerald-600">{stats.completed}</p><p className="text-xs text-slate-500">Completed</p></div>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center"><Clock className="w-5 h-5 text-amber-600" /></div>
          <div><p className="text-2xl font-bold text-amber-600">{stats.processing}</p><p className="text-xs text-slate-500">Processing</p></div>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center"><AlertCircle className="w-5 h-5 text-red-500" /></div>
          <div><p className="text-2xl font-bold text-red-500">{stats.pending}</p><p className="text-xs text-slate-500">Pending</p></div>
        </CardContent></Card>
      </div>

      {/* Upload Section */}
      <Card className="border-slate-200 mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Upload Lab Reports</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Processing Lab Tabs */}
          {processingLabs.length > 0 && (
            <div className="flex gap-2 mb-4 flex-wrap">
              {processingLabs.map(lab => (
                <button
                  key={lab.id}
                  onClick={() => setSelectedLabForUpload(lab.name)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedLabForUpload === lab.name ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  data-testid={`lab-tab-${lab.name.replace(/\s+/g, '-')}`}
                >
                  {lab.name}
                </button>
              ))}
            </div>
          )}

          {!letterhead && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-center justify-between" data-testid="letterhead-not-configured-banner">
              <span>Set up your letterhead so every uploaded report is automatically branded with your logo & details.</span>
              <Button size="sm" variant="outline" className="h-7 text-xs shrink-0 ml-2" onClick={() => setShowSettings(true)}>Set up now</Button>
            </div>
          )}

          <div className="mb-3">
            <label className="text-xs font-medium text-slate-600">Patient Phone Number (optional - can add later)</label>
            <Input
              value={uploadPhone}
              onChange={e => setUploadPhone(e.target.value)}
              placeholder="e.g. 9876543210 - needed only to WhatsApp share the report"
              className="h-9 text-sm mt-1"
              data-testid="upload-patient-phone-input"
            />
          </div>

          {/* Drop Zone */}
          <div
            className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all"
            onClick={() => { const input = document.getElementById('pdf-upload-input'); input?.click(); }}
            onDragOver={e => e.preventDefault()}
            onDrop={handleFileDrop}
            data-testid="pdf-drop-zone"
          >
            <Upload className={`w-10 h-10 mx-auto mb-3 ${uploading ? 'text-blue-500 animate-pulse' : 'text-slate-400'}`} />
            {uploading ? (
              <p className="text-sm text-blue-600 font-medium">Uploading...</p>
            ) : (
              <>
                <p className="text-sm font-medium text-slate-700">
                  Drop one or more PDFs for {selectedLabForUpload || 'selected lab'}
                </p>
                <p className="text-xs text-slate-400 mt-1">Click or drag multiple PDF files here at once. AI will analyze each report and apply your letterhead automatically.</p>
              </>
            )}
          </div>
          <input
            id="pdf-upload-input"
            type="file"
            multiple
            accept=".pdf"
            className="hidden"
            onChange={e => uploadFiles(Array.from(e.target.files || []))}
          />
          <input ref={fileInputRef} type="file" accept=".pdf" className="hidden" />

          {/* Live upload progress */}
          {Object.keys(uploadProgress).length > 0 && (
            <div className="mt-4 space-y-2" data-testid="upload-progress-list">
              {Object.entries(uploadProgress).map(([key, p]) => (
                <div key={key} className="flex items-center gap-3" data-testid={`upload-progress-${key}`}>
                  <span className="text-xs text-slate-600 truncate max-w-[160px] shrink-0">{p.name}</span>
                  <Progress value={p.percent} className="h-2 flex-1" />
                  <span className="text-xs font-semibold text-blue-600 w-10 text-right shrink-0" data-testid={`upload-progress-percent-${key}`}>{p.percent}%</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Reports from Bills */}
      {pendingBills.length > 0 && (
        <Card className="border-slate-200 mb-6" data-testid="pending-bills-section">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Pending Report Uploads from Bills</CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">{pendingBills.length} pending</Badge>
                <Button variant="outline" size="sm" className="h-6 text-xs" onClick={clearAllPendingBills} data-testid="clear-all-pending-bills-btn">
                  <X className="w-3 h-3 mr-1" /> Clear All
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {pendingBills.map(bill => (
                <div key={bill.id} className="flex items-center justify-between p-2 bg-amber-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-800">#{bill.reg_no} - {bill.patient_name}</p>
                    <p className="text-xs text-slate-500">
                      {bill.processing_lab && <span className="mr-2">{bill.processing_lab}</span>}
                      {(bill.items || []).map(i => i.item_name).join(', ').slice(0, 60)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => uploadForBill(bill)} disabled={uploading} className="h-7 text-xs" data-testid={`upload-for-bill-${bill.reg_no}`}>
                      <Upload className="w-3 h-3 mr-1" /> Upload
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => dismissPendingBill(bill.id)} className="h-7 w-7 p-0" data-testid={`dismiss-pending-bill-${bill.reg_no}`}>
                      <X className="w-3.5 h-3.5 text-slate-400" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reports List */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm">All Reports ({total})</CardTitle>
            {processingLabs.length > 0 && (
              <div className="flex gap-1">
                <button onClick={() => { setLabFilter(''); setPage(1); }} className={`px-2 py-1 text-xs rounded ${!labFilter ? 'bg-blue-100 text-blue-700' : 'text-slate-500 hover:bg-slate-100'}`}>All</button>
                {processingLabs.map(l => (
                  <button key={l.id} onClick={() => { setLabFilter(l.name); setPage(1); }} className={`px-2 py-1 text-xs rounded ${labFilter === l.name ? 'bg-blue-100 text-blue-700' : 'text-slate-500 hover:bg-slate-100'}`}>{l.name}</button>
                ))}
              </div>
            )}
          </div>
          <div className="flex gap-1 mt-2" data-testid="view-filter-bar">
            {[
              { key: 'all', label: 'All' },
              { key: 'sent', label: 'Sent' },
              { key: 'not_sent', label: 'Not Sent' }
            ].map(f => (
              <button
                key={f.key}
                onClick={() => { setViewFilter(f.key); setPage(1); }}
                className={`px-2.5 py-1 text-xs rounded-full border ${viewFilter === f.key ? 'bg-slate-800 text-white border-slate-800' : 'text-slate-500 border-slate-200 hover:bg-slate-100'}`}
                data-testid={`view-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
              <Input placeholder="Search by patient, filename, phone..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-8 h-8 text-sm" data-testid="report-search-input" />
            </div>
            <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} className="w-[140px] h-8 text-sm" data-testid="report-date-from-input" />
            <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} className="w-[140px] h-8 text-sm" data-testid="report-date-to-input" />
          </div>
          {selectedIds.length > 0 && (
            <div className="mt-2 flex items-center justify-between p-2 bg-red-50 border border-red-200 rounded-lg" data-testid="reports-selection-bar">
              <span className="text-xs font-medium text-red-700">{selectedIds.length} selected</span>
              <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={bulkDeleteReports} data-testid="bulk-delete-reports-btn">
                <Trash2 className="w-3 h-3 mr-1" /> Delete Selected
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {reports.length > 0 && (
            <div className="flex items-center gap-2 px-1 mb-1">
              <input type="checkbox" checked={selectedIds.length === reports.length} onChange={toggleSelectAll} data-testid="select-all-reports-checkbox" />
              <span className="text-xs text-slate-400">Select all on this page</span>
            </div>
          )}
          <div className="space-y-2">
            {reports.map(r => (
              <div
                key={r.id}
                className={`p-3 border rounded-lg flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors ${selectedReport?.id === r.id ? 'border-blue-300 bg-blue-50' : 'border-slate-200'}`}
                onClick={() => viewReport(r)}
                data-testid={`report-row-${r.id}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <input type="checkbox" checked={selectedIds.includes(r.id)} onClick={e => e.stopPropagation()} onChange={() => toggleSelect(r.id)} data-testid={`select-report-${r.id}`} />
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${r.status === 'completed' ? 'bg-emerald-50' : r.status === 'processing' ? 'bg-amber-50' : 'bg-red-50'}`}>
                    {r.status === 'completed' ? <CheckCircle className="w-5 h-5 text-emerald-500" /> :
                     r.status === 'processing' ? <span className="text-[11px] font-bold text-amber-600" data-testid={`processing-percent-${r.id}`}>{getProcessingPercent(r)}%</span> :
                     <AlertCircle className="w-5 h-5 text-red-500" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{r.patient_name || r.original_filename}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {r.status === 'processing' && <span className="text-[10px] text-amber-600 font-medium">AI analyzing... {getProcessingPercent(r)}%</span>}
                      {r.lab_type && <Badge variant="outline" className="text-[10px]">{r.lab_type}</Badge>}
                      {r.branded && <Badge className="text-[10px] bg-teal-100 text-teal-700 hover:bg-teal-100">Branded</Badge>}
                      {r.status === 'completed' && (r.whatsapp_sent
                        ? <Badge className="text-[10px] bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid={`whatsapp-sent-badge-${r.id}`}>Sent on WhatsApp</Badge>
                        : <Badge variant="outline" className="text-[10px] text-slate-400" data-testid={`whatsapp-not-sent-badge-${r.id}`}>Not Sent</Badge>)}
                      <span className="text-xs text-slate-400">{formatSize(r.size)}</span>
                      <span className="text-xs text-slate-400">{r.created_at?.slice(0, 10)}</span>
                      {r.bill_id && <span className="text-xs text-blue-500">Bill linked</span>}
                      <button
                        onClick={e => { e.stopPropagation(); setEditPhone({ report: r, value: r.patient_phone || '' }); }}
                        className="text-xs text-slate-500 hover:text-blue-600 flex items-center gap-0.5"
                        data-testid={`edit-phone-report-${r.id}`}
                      >
                        {r.patient_phone || 'Add phone'} <Pencil className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); openPreview(r); }} className="h-8 w-8 p-0" data-testid={`preview-report-${r.id}`}><Eye className="w-4 h-4 text-slate-500" /></Button>
                  <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); downloadReport(r); }} className="h-8 w-8 p-0" data-testid={`download-report-${r.id}`}><Download className="w-4 h-4 text-slate-500" /></Button>
                  <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); shareViaWhatsApp(r); }} className="h-8 w-8 p-0" data-testid={`whatsapp-share-report-${r.id}`}><MessageCircle className="w-4 h-4 text-emerald-500" /></Button>
                  <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); deleteReport(r.id); }} className="h-8 w-8 p-0" data-testid={`delete-report-${r.id}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                </div>
              </div>
            ))}
            {reports.length === 0 && (
              <div className="text-center py-8 text-slate-400">
                <FileText className="w-8 h-8 mx-auto mb-3 opacity-40" />
                <p className="text-sm">No reports yet. Upload PDF lab reports above.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* AI Analysis Panel */}
      {selectedReport && selectedReport.ai_analysis && (
        <Dialog open={!!selectedReport} onOpenChange={() => setSelectedReport(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ai-analysis-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                AI Health Analysis
              </DialogTitle>
              <p className="text-xs text-slate-500">{selectedReport.patient_name || selectedReport.original_filename}</p>
            </DialogHeader>
            <div className="space-y-4">
              {selectedReport.ai_analysis.summary && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="text-sm font-semibold text-blue-800 mb-1 flex items-center gap-1"><Heart className="w-4 h-4" /> Summary</h3>
                  <p className="text-sm text-blue-700">{selectedReport.ai_analysis.summary}</p>
                </div>
              )}
              {selectedReport.ai_analysis.health_concerns?.length > 0 && (
                <div className="p-4 bg-red-50 rounded-lg">
                  <h3 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> Health Concerns</h3>
                  <ul className="space-y-1">{selectedReport.ai_analysis.health_concerns.map((c, i) => (
                    <li key={`li-${i}`} className="text-sm text-red-700 flex items-start gap-2"><span className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5 shrink-0" />{c}</li>
                  ))}</ul>
                </div>
              )}
              {selectedReport.ai_analysis.diet_plan?.length > 0 && (
                <div className="p-4 bg-green-50 rounded-lg">
                  <h3 className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-1"><Utensils className="w-4 h-4" /> Diet Recommendations</h3>
                  <ul className="space-y-1">{selectedReport.ai_analysis.diet_plan.map((d, i) => (
                    <li key={`li-${i}`} className="text-sm text-green-700 flex items-start gap-2"><span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 shrink-0" />{d}</li>
                  ))}</ul>
                </div>
              )}
              {(selectedReport.ai_analysis.repeat_tests_monthly?.length > 0 || selectedReport.ai_analysis.repeat_tests_6_months?.length > 0 || selectedReport.ai_analysis.repeat_tests_yearly?.length > 0) && (
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h3 className="text-sm font-semibold text-purple-800 mb-2 flex items-center gap-1"><CalendarDays className="w-4 h-4" /> Repeat Tests Schedule</h3>
                  {selectedReport.ai_analysis.repeat_tests_monthly?.length > 0 && (
                    <div className="mb-2"><p className="text-xs font-semibold text-purple-700 mb-1">Monthly</p>
                    <div className="flex flex-wrap gap-1">{selectedReport.ai_analysis.repeat_tests_monthly.map((t, i) => <Badge key={`${t}-${i}`} variant="outline" className="text-[10px] bg-white">{t}</Badge>)}</div></div>
                  )}
                  {selectedReport.ai_analysis.repeat_tests_6_months?.length > 0 && (
                    <div className="mb-2"><p className="text-xs font-semibold text-purple-700 mb-1">Every 6 Months</p>
                    <div className="flex flex-wrap gap-1">{selectedReport.ai_analysis.repeat_tests_6_months.map((t, i) => <Badge key={`${t}-${i}`} variant="outline" className="text-[10px] bg-white">{t}</Badge>)}</div></div>
                  )}
                  {selectedReport.ai_analysis.repeat_tests_yearly?.length > 0 && (
                    <div><p className="text-xs font-semibold text-purple-700 mb-1">Yearly</p>
                    <div className="flex flex-wrap gap-1">{selectedReport.ai_analysis.repeat_tests_yearly.map((t, i) => <Badge key={`${t}-${i}`} variant="outline" className="text-[10px] bg-white">{t}</Badge>)}</div></div>
                  )}
                </div>
              )}
              {selectedReport.ai_analysis.interpretation && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h3 className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-1"><Stethoscope className="w-4 h-4" /> Detailed Interpretation</h3>
                  <p className="text-sm text-slate-600 whitespace-pre-line">{selectedReport.ai_analysis.interpretation}</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => openPreview(selectedReport)}><Eye className="w-3.5 h-3.5 mr-1" /> View PDF</Button>
              <Button variant="outline" size="sm" onClick={() => downloadReport(selectedReport)}><Download className="w-3.5 h-3.5 mr-1" /> Download</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* PDF Preview */}
      {(previewUrl || previewLoading) && (
        <Dialog open={!!(previewUrl || previewLoading)} onOpenChange={() => { if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
          <DialogContent className="max-w-4xl max-h-[90vh]" data-testid="pdf-preview-dialog">
            <DialogHeader><DialogTitle className="text-sm">Report Preview</DialogTitle></DialogHeader>
            {previewLoading ? (
              <div className="w-full h-[70vh] flex items-center justify-center text-slate-400 text-sm">Loading preview...</div>
            ) : (
              <iframe src={previewUrl} className="w-full h-[70vh] border rounded" title="Report" />
            )}
            <DialogFooter>
              <Button variant="outline" size="sm" data-testid="close-preview-btn" onClick={() => { if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit patient phone on an existing report */}
      <Dialog open={!!editPhone} onOpenChange={(v) => { if (!v) setEditPhone(null); }}>
        <DialogContent className="max-w-sm" data-testid="edit-phone-dialog">
          <DialogHeader><DialogTitle>Edit Patient Phone</DialogTitle></DialogHeader>
          <Input
            value={editPhone?.value || ''}
            onChange={e => setEditPhone(prev => ({ ...prev, value: e.target.value }))}
            placeholder="Patient phone number"
            data-testid="edit-phone-value-input"
          />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditPhone(null)}>Cancel</Button>
            <Button size="sm" onClick={saveEditPhone} data-testid="save-edit-phone-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog - My Letterhead */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="letterhead-settings-dialog">
          <DialogHeader><DialogTitle>My Letterhead</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 mb-2">
            Every report you upload (from any processing lab, any layout) is automatically overlaid with your logo & details below,
            plus 2 AI-generated summary pages and your Terms & Conditions page.
          </p>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-600">Lab / Company Name *</label>
                <Input value={lhForm.lab_name} onChange={e => setLhForm({ ...lhForm, lab_name: e.target.value })} className="h-9 text-sm mt-1" data-testid="lh-lab-name-input" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-600">Tagline</label>
                <Input value={lhForm.tagline} onChange={e => setLhForm({ ...lhForm, tagline: e.target.value })} placeholder="e.g. Precision You Can Trust" className="h-9 text-sm mt-1" data-testid="lh-tagline-input" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-600">Accreditations</label>
                <Input value={lhForm.accreditations} onChange={e => setLhForm({ ...lhForm, accreditations: e.target.value })} placeholder="e.g. NABL Accredited | ISO 9001:2015" className="h-9 text-sm mt-1" data-testid="lh-accreditations-input" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-600">Address</label>
                <Input value={lhForm.address} onChange={e => setLhForm({ ...lhForm, address: e.target.value })} className="h-9 text-sm mt-1" data-testid="lh-address-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Phone</label>
                <Input value={lhForm.phone} onChange={e => setLhForm({ ...lhForm, phone: e.target.value })} className="h-9 text-sm mt-1" data-testid="lh-phone-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Email</label>
                <Input value={lhForm.email} onChange={e => setLhForm({ ...lhForm, email: e.target.value })} className="h-9 text-sm mt-1" data-testid="lh-email-input" />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Logo</label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="file" accept="image/*" onChange={e => onLogoSelected(e.target.files?.[0])} className="flex-1 h-9 text-sm" data-testid="lh-logo-input" />
                {letterhead?.logo_storage_path && !lhLogoFile && <Badge variant="secondary" className="text-[10px] shrink-0">Logo set</Badge>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Accreditation Logo 1</label>
                <p className="text-[10px] text-slate-400 mb-1">e.g. NABL badge</p>
                <div className="flex items-center gap-1.5">
                  <Input type="file" accept="image/*" onChange={e => setLhAccredLogo1File(e.target.files?.[0])} className="flex-1 h-9 text-xs" data-testid="lh-accred-logo1-input" />
                  {letterhead?.accreditation_logo_1_path && !lhAccredLogo1File && <Badge variant="secondary" className="text-[9px] shrink-0">Set</Badge>}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Accreditation Logo 2</label>
                <p className="text-[10px] text-slate-400 mb-1">e.g. ISO badge</p>
                <div className="flex items-center gap-1.5">
                  <Input type="file" accept="image/*" onChange={e => setLhAccredLogo2File(e.target.files?.[0])} className="flex-1 h-9 text-xs" data-testid="lh-accred-logo2-input" />
                  {letterhead?.accreditation_logo_2_path && !lhAccredLogo2File && <Badge variant="secondary" className="text-[9px] shrink-0">Set</Badge>}
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Brand Accent Color</label>
              <p className="text-xs text-slate-400 mb-1">Auto-suggested from your logo. Adjust it to match your brand.</p>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={lhForm.brand_color}
                  onChange={e => setLhForm({ ...lhForm, brand_color: e.target.value })}
                  className="h-9 w-14 rounded border border-slate-200 cursor-pointer"
                  data-testid="lh-brand-color-input"
                />
                <Input value={lhForm.brand_color} onChange={e => setLhForm({ ...lhForm, brand_color: e.target.value })} className="flex-1 h-9 text-sm font-mono" data-testid="lh-brand-color-text" />
              </div>
            </div>

            <div className="border-t pt-3">
              <label className="text-xs font-medium text-slate-600">Google Review Link</label>
              <p className="text-[10px] text-slate-400 mb-1">Shared with patients over WhatsApp along with their report, to encourage Google reviews.</p>
              <Input value={lhForm.google_review_link} onChange={e => setLhForm({ ...lhForm, google_review_link: e.target.value })} placeholder="https://g.page/r/your-lab/review" className="h-9 text-sm" data-testid="lh-google-review-input" />
            </div>

            <div className="border-t pt-3">
              <label className="text-xs font-medium text-slate-600">WhatsApp Message Template</label>
              <p className="text-[10px] text-slate-400 mb-1">
                Sent to the patient along with the report link. Placeholders: {'{patient_name}'}, {'{lab_name}'}, {'{tagline}'}, {'{download_url}'}, {'{contact}'}, {'{review_link}'}
              </p>
              <Textarea
                value={lhForm.whatsapp_message_template}
                onChange={e => setLhForm({ ...lhForm, whatsapp_message_template: e.target.value })}
                placeholder={DEFAULT_WHATSAPP_TEMPLATE}
                rows={5}
                className="text-sm font-mono"
                data-testid="lh-whatsapp-template-input"
              />
              <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1 mt-1" onClick={() => setLhForm({ ...lhForm, whatsapp_message_template: DEFAULT_WHATSAPP_TEMPLATE })} data-testid="reset-whatsapp-template-btn">
                Reset to default
              </Button>
            </div>

            <div className="border-t pt-3">
              <p className="text-sm font-medium mb-1">Footer Text</p>
              <p className="text-xs text-slate-400 mb-2">Use {'{lab_name}'} and {'{date}'} as placeholders. Leave blank for the default.</p>
              <div className="space-y-2">
                <div>
                  <label className="text-xs font-medium text-slate-600">Footer Left Text</label>
                  <Input value={lhForm.footer_left_text} onChange={e => setLhForm({ ...lhForm, footer_left_text: e.target.value })} placeholder="Report reviewed & issued by {lab_name}" className="h-9 text-sm mt-1" data-testid="lh-footer-left-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Footer Right Text</label>
                  <Input value={lhForm.footer_right_text} onChange={e => setLhForm({ ...lhForm, footer_right_text: e.target.value })} placeholder="Generated {date}" className="h-9 text-sm mt-1" data-testid="lh-footer-right-input" />
                </div>
              </div>
            </div>

            <div className="border-t pt-3">
              <p className="text-xs text-slate-400">
                Note: We automatically detect and remove your processing lab's own header/footer from each uploaded report before applying your letterhead - no setup needed.
              </p>
            </div>

            <Button size="sm" className="w-full" onClick={saveLetterhead} disabled={savingLetterhead} data-testid="save-letterhead-btn">
              {savingLetterhead ? 'Saving...' : letterhead ? 'Update Letterhead' : 'Save Letterhead'}
            </Button>

            {/* Terms & Conditions page */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-1">Terms & Conditions Page</p>
              <p className="text-xs text-slate-400 mb-2">Upload an image or single-page PDF. It will be appended as the last page of every generated report.</p>
              <div className="flex items-center gap-2 mb-2">
                {letterhead?.terms_page_storage_path ? (
                  <Badge className="text-[10px] bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{letterhead.terms_page_filename || 'Terms page set'}</Badge>
                ) : (
                  <Badge variant="secondary" className="text-[10px]">Not set</Badge>
                )}
              </div>
              <div className="flex gap-2">
                <Input type="file" accept="image/*,.pdf" onChange={e => setLhTermsFile(e.target.files?.[0])} className="flex-1 h-9 text-sm" data-testid="lh-terms-input" />
                <Button size="sm" onClick={uploadTermsPage} disabled={uploadingTerms || !lhTermsFile} data-testid="upload-terms-page-btn">
                  {uploadingTerms ? '...' : 'Upload'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
